import { sagaIntl } from '@i18n';
import { UPDATE_COMMENT, UPDATE_COMMENT_SUCCESS } from '../actions';
import { produce, produceWithPatches, WritableDraft } from 'immer';
import { uniq } from 'lodash';
import { MainStore } from '../store';
import { current } from '@reduxjs/toolkit';
import {
  FetchMediaCommentsResponse,
  FetchMediaUserResponse,
} from '@common-modules/mediaDetails/services';
import { GetActionCreatorPayloadT } from '@utils';
import { TDOId } from '../../universal/models/Brands';

export const fetchMediaComments = (state: WritableDraft<MainStore>) => ({
  ...state,
  isFetchingComments: true,
});

export const fetchMediaCommentsSuccess = (
  state: WritableDraft<MainStore>,
  {
    payload: {
      structuredDataObjects: { offset, records },
    },
  }: {
    payload: FetchMediaCommentsResponse;
  }
) => ({
  ...state,
  mediaComments: Object.values(records).reduce(
    (prev, { data }) => ({ ...prev, [data.commentId]: data }),
    offset
      ? { ...state.mediaComments }
      : Object.entries(state.mediaComments).reduce(
          (drafts, [commentId, comment]) =>
            comment.done ? drafts : { ...drafts, [commentId]: comment },
          {} as MainStore['mediaComments']
        )
  ),
  isFetchingComments: false,
});

export const addNewComment = (
  state: WritableDraft<MainStore>,
  {
    payload,
  }: {
    payload: {
      commentId: string;
      tdoId: TDOId;
      userId: string;
      searchName: string;
      currentTime: number;
    };
  }
) => {
  state.mediaComments[payload.commentId] = {
    id: payload.commentId,
    comment: '',
    done: false,
    read: false,
    deleted: false,
    archived: false,
    createdDateTime: '',
    modifiedDateTime: '',
    tdoId: payload.tdoId,
    createdBy: payload.userId,
    commentId: payload.commentId,
    searchName: payload.searchName,
    mediaTimestamp: payload.currentTime,
    isCancelledNewComment: true,
  };
};
// ({
//   ...state,
//   mediaComments: {
//     [payload.commentId]: {
//       comment: '',
//       done: false,
//       read: false,
//       deleted: false,
//       archived: false,
//       createdDateTime: '',
//       modifiedDateTime: '',
//       tdoId: payload.tdoId,
//       createdBy: payload.userId,
//       commentId: payload.commentId,
//       searchName: payload.searchName,
//       mediaTimestamp: payload.currentTime,
//     },
//     ...state.mediaComments,
//   },
// });

export const updateCommentSuccess = (
  state: WritableDraft<MainStore>,
  {
    payload,
  }: { payload: GetActionCreatorPayloadT<typeof UPDATE_COMMENT_SUCCESS> }
) => {
  const {
    createStructuredData: { data },
    isUndo,
  } = payload;
  const intl = sagaIntl();
  const currentState = current(state);
  const previousComment = currentState.mediaComments[data.commentId];
  const modify = previousComment && previousComment.createdDateTime !== '';
  const prevHidden = previousComment?.archived;
  const nextHidden = data.archived;
  const markAsRead = data.read;

  return produce(state, (draft) => {
    draft.mediaComments[data.commentId] = data;
    if (!isUndo && draft.mediaCommentDrafts[data.commentId] === data.comment) {
      delete draft.mediaCommentDrafts[data.commentId];
    }

    if (isUndo) {
      return;
    }

    if (!isUndo) {
      let description = '';

      if (!modify) {
        description = intl.formatMessage({
          id: 'addComment',
          defaultMessage: 'Add comment',
        });

        draft.history.past.push({
          action: {
            creator: UPDATE_COMMENT,
            payload: { comment: data, isUndo: true },
          },
          inverseAction: {
            creator: UPDATE_COMMENT,
            payload: {
              comment: { ...data, deleted: true, archived: true },
              modify: true,
              isUndo: true,
            },
          },
          metadata: { description },
        });
      } else {
        // must be modifying an existing comment

        if (prevHidden === false && nextHidden === true) {
          description = intl.formatMessage({
            id: 'hideComment',
            defaultMessage: 'hide comment',
          });
        } else if (prevHidden === true && nextHidden === false) {
          description = intl.formatMessage({
            id: 'unhideComment',
            defaultMessage: 'unhide comment',
          });
        } else if (previousComment?.read !== markAsRead) {
          description = intl.formatMessage({
            id: 'markCommentAsRead',
            defaultMessage: 'mark comment as read',
          });
        } else {
          description = intl.formatMessage({
            id: 'updateComment',
            defaultMessage: 'update comment',
          });
        }

        draft.history.past.push({
          action: {
            creator: UPDATE_COMMENT,
            payload: { comment: data, isUndo: true },
          },
          inverseAction: {
            creator: UPDATE_COMMENT,
            payload: {
              comment: previousComment,
              isUndo: true,
            },
          },
          metadata: { description },
        });
      }

      draft.history.future = [];
    }
  });
};

export const setMediaCommentDraft = (
  state: WritableDraft<MainStore>,
  { payload }: { payload: { commentId: string; comment: string } }
) => {
  state.mediaCommentDrafts[payload.commentId] = payload.comment;
};

export const clearMediaCommentDraft = (
  state: WritableDraft<MainStore>,
  { payload }: { payload: { commentId: string } }
) => {
  delete state.mediaCommentDrafts[payload.commentId];
};

export const setEditingMediaComment = (
  state: WritableDraft<MainStore>,
  { payload }: { payload: { commentId: string | null } }
) => {
  state.editingMediaCommentId = payload.commentId;
};

export const removeComment = (
  state: WritableDraft<MainStore>,
  {
    payload,
  }: {
    payload: {
      commentId: string;
      isCancelledNewComment?: boolean;
      isUndo?: boolean;
    };
  }
) => {
  if (payload.isCancelledNewComment || payload.isUndo) {
    delete state.mediaComments[payload.commentId];
    delete state.mediaCommentDrafts[payload.commentId];
    return;
  }

  const intl = sagaIntl();

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      delete draft.mediaComments[payload.commentId];
      delete draft.mediaCommentDrafts[payload.commentId];
    }
  );

  return produce(nextState, (draft) => {
    if (patches.length && inversePatches.length) {
      draft.history.past.push({
        patches,
        inversePatches,
        metadata: {
          description: intl.formatMessage({
            id: 'removeComment',
            defaultMessage: 'remove comment',
          }),
        },
      });
      draft.history.future = [];
    }
  });
};

// export const fetchMediaCommentsUserSuccess = (
//   state: MainStore,
//   {
//     payload,
//   }: {
//     payload: {
//       basicUserInfo: {
//         id: string;
//         firstName: string;
//         lastName: string;
//         imageUrl: string;
//       };
//     };
//   }
// ) => ({
//   ...state,
//   mediaCommentsUsers: {
//     ...state.mediaCommentsUsers,
//     [payload.basicUserInfo.id]: {
//       firstName: payload.basicUserInfo.firstName,
//       lastName: payload.basicUserInfo.lastName,
//       imageUrl: payload.basicUserInfo.imageUrl,
//     },
//   },
// });

export const fetchMediaCommentsUserSuccess = (
  state: WritableDraft<MainStore>,
  {
    payload,
  }: {
    payload: {
      basicUserInfo: {
        id: string;
        firstName: string;
        lastName: string;
        imageUrl: string | null;
      };
    };
  }
) => {
  state.mediaCommentsUsers[payload.basicUserInfo.id] = payload.basicUserInfo;
};

export const selectComment = (
  state: WritableDraft<MainStore>,
  { payload }: { payload: { commentId: string } }
) => {
  const { commentId } = payload;
  const { selectedMediaComments: selected } = state;
  const index = selected.indexOf(commentId);
  return {
    ...state,
    selectedMediaComments:
      index > -1
        ? [...selected.slice(0, index), ...selected.slice(index + 1)]
        : [...selected, commentId],
  };
};

export const selectCommentsOnTimeline = (
  state: WritableDraft<MainStore>,
  { payload }: { payload: { commentId: string } }
) => {
  const { commentId } = payload;
  const { selectedMediaComments, mediaComments } = state;

  const mediaTimeStamp = mediaComments[commentId]?.mediaTimestamp;
  const commentsAtTimestamp = Object.values(mediaComments)
    .filter((comment) => comment.mediaTimestamp === mediaTimeStamp)
    .map((comment) => comment.commentId);

  const isSelected =
    selectedMediaComments.filter((comment) =>
      commentsAtTimestamp.includes(comment)
    ).length === commentsAtTimestamp.length;

  return {
    ...state,
    selectedMediaComments: isSelected
      ? [
          ...selectedMediaComments.filter(
            (comment) => !commentsAtTimestamp.includes(comment)
          ),
        ]
      : uniq([...commentsAtTimestamp]),
  };
};

export const fetchMediaUserSuccess = (
  state: WritableDraft<MainStore>,
  { payload }: { payload: FetchMediaUserResponse }
) => ({
  ...state,
  user: payload.me,
});
// })
//   state.user = payload.me;
//   return;
// }

export const basicUserQueryIsSupported = (state: WritableDraft<MainStore>) => ({
  ...state,
  isBasicUserQuerySupported: false,
});

export const updateStatusHideComment = (
  state: WritableDraft<MainStore>,
  { payload }: { payload: { isHideComment: boolean } }
) => {
  const { isHideComment } = payload;

  return {
    ...state,
    isHideComment,
  };
};
